import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  StepIndicator, PrimaryButton, SecondaryButton, InputField, SelectField,
  ErrorMessage, LoadingOverlay, SuccessModal, AutoSaveIndicator, 
  PasswordRequirements, FormCard, LeftSection, useAutoSave, FIELD_LIMITS, REGEX
} from './DesignSystem';

const FundraiserForm = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    purpose: '',
    name: '',
    email: '',
    password: '',
    mobile: ''
  });

  const [fieldErrors, setFieldErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState('');
  const [showPurposeDropdown, setShowPurposeDropdown] = useState(false);
  const [selectedPurpose, setSelectedPurpose] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [currentFeature, setCurrentFeature] = useState(0);
  const [isFormValid, setIsFormValid] = useState(false);
  
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);

  // Auto-save setup
  const { lastSaved, isSaving, saveError, loadDraft, clearDraft } = useAutoSave(
    formData, 
    'fundraiser_registration'
  );

  const purposeOptions = [
    { value: 'medical', label: 'Medical Expenses', icon: '❤️', color: 'from-red-400 to-pink-500', description: 'For medical treatments and healthcare' },
    { value: 'education', label: 'Education Fees', icon: '🎓', color: 'from-blue-400 to-cyan-500', description: 'For tuition fees and books' },
    { value: 'emergency', label: 'Emergency Relief', icon: '🚨', color: 'from-orange-400 to-red-500', description: 'For urgent help during crises' },
    { value: 'business', label: 'Business Funding', icon: '💼', color: 'from-green-400 to-emerald-500', description: 'For startup capital' },
    { value: 'personal', label: 'Personal Needs', icon: '👤', color: 'from-purple-400 to-indigo-500', description: 'For personal projects' },
    { value: 'creative', label: 'Creative Projects', icon: '🎨', color: 'from-pink-400 to-rose-500', description: 'For art, music, and film' },
    { value: 'community', label: 'Community Support', icon: '👥', color: 'from-teal-400 to-cyan-500', description: 'For community development' },
    { value: 'animals', label: 'Animal Welfare', icon: '🐾', color: 'from-yellow-400 to-amber-500', description: 'For animal rescue' },
  ];

  const features = [
    { 
      title: "Quick Setup", 
      description: "Start fundraising in just 2 simple steps", 
      gradient: "from-green-500 to-emerald-600",
      icon: "⚡"
    },
    { 
      title: "Zero Platform Fees", 
      description: "100% of donations go to your cause", 
      gradient: "from-gsg-teal to-gsg-teal-dark",
      icon: "💰"
    },
    { 
      title: "Global Support", 
      description: "Reach donors from around the world", 
      gradient: "from-purple-500 to-indigo-600",
      icon: "🌍"
    }
  ];

  // Initialize - Clear on refresh
  useEffect(() => {
    const featureInterval = setInterval(() => {
      setCurrentFeature(prev => (prev + 1) % features.length);
    }, 4000);

    // Clear form data on refresh
    const handlePageRefresh = () => {
      const wasRefreshed = performance.navigation.type === 1 || 
                          performance.getEntriesByType('navigation')[0]?.type === 'reload';
      
      if (wasRefreshed) {
        clearDraft();
        setFormData({
          purpose: '',
          name: '',
          email: '',
          password: '',
          mobile: ''
        });
        setSelectedPurpose(null);
        setTouchedFields({});
        setFieldErrors({});
        setIsFormValid(false);
      } else {
        // Load draft only if not a refresh
        const savedDraft = loadDraft();
        if (savedDraft) {
          setFormData(savedDraft);
          if (savedDraft.purpose) {
            const purpose = purposeOptions.find(p => p.value === savedDraft.purpose);
            if (purpose) setSelectedPurpose(purpose);
          }
          setApiError({ type: 'info', message: 'Loaded draft from previous session' });
          setTimeout(() => setApiError(''), 5000);
        }
      }
    };

    handlePageRefresh();

    // Close dropdown when clicking outside
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
          triggerRef.current && !triggerRef.current.contains(event.target)) {
        setShowPurposeDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    
    return () => {
      clearInterval(featureInterval);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Clear form on page refresh/hard reload
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Clear the draft when page is being refreshed
      sessionStorage.setItem('isRefreshing', 'true');
    };

    const handlePageLoad = () => {
      if (sessionStorage.getItem('isRefreshing') === 'true') {
        clearDraft();
        sessionStorage.removeItem('isRefreshing');
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('load', handlePageLoad);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('load', handlePageLoad);
    };
  }, [clearDraft]);

  // Validate form whenever formData changes
  useEffect(() => {
    validateAllFields();
  }, [formData]);

  // Validation functions
  const validateField = (name, value) => {
    let error = '';
    
    switch (name) {
      case 'purpose':
        if (!value.trim()) error = 'Please select a purpose';
        break;
      case 'name':
        if (!value.trim()) error = 'Name is required';
        else if (value.length < FIELD_LIMITS.name.min) error = `Name must be at least ${FIELD_LIMITS.name.min} characters`;
        break;
      case 'email':
        if (!value.trim()) error = 'Email is required';
        else if (!REGEX.email.test(value)) error = 'Please enter a valid email address';
        break;
      case 'password':
        if (!value) error = 'Password is required';
        else if (value.length < 8) error = 'Password must be at least 8 characters';
        else if (!/[A-Z]/.test(value)) error = 'Must contain uppercase letter';
        else if (!/[a-z]/.test(value)) error = 'Must contain lowercase letter';
        else if (!/\d/.test(value)) error = 'Must contain a number';
        else if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(value)) error = 'Must contain special character';
        break;
      case 'mobile':
        if (!value) error = 'Mobile number is required';
        else if (!REGEX.mobile.test(value)) error = 'Mobile number must be 10 digits';
        break;
      default:
        break;
    }
    
    return error;
  };

  const validateAllFields = () => {
    const newFieldErrors = {};
    let isValid = true;

    // Validate only touched fields or all fields when submitting
    const fieldsToValidate = ['purpose', 'name', 'email', 'password', 'mobile'];
    
    fieldsToValidate.forEach(field => {
      const error = validateField(field, formData[field]);
      // Only show error if field has been touched or has a value
      if (error && (touchedFields[field] || formData[field].trim())) {
        newFieldErrors[field] = error;
        isValid = false;
      }
    });

    setFieldErrors(newFieldErrors);
    setIsFormValid(isValid);
    return isValid;
  };

  const handleBlur = (field) => {
    // Mark field as touched
    setTouchedFields(prev => ({ ...prev, [field]: true }));
    
    const error = validateField(field, formData[field]);
    setFieldErrors(prev => ({ ...prev, [field]: error }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const limit = FIELD_LIMITS[name]?.max;
    
    if (limit && value.length > limit) {
      return;
    }
    
    setFormData(prev => ({ ...prev, [name]: value }));
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({ ...prev, [name]: '' }));
    }
    if (apiError) {
      setApiError('');
    }
  };

  const handlePurposeSelect = (purpose) => {
    setSelectedPurpose(purpose);
    setFormData(prev => ({ ...prev, purpose: purpose.value }));
    setShowPurposeDropdown(false);
    
    // Mark purpose as touched
    setTouchedFields(prev => ({ ...prev, purpose: true }));
    
    // Clear error for purpose
    if (fieldErrors.purpose) {
      setFieldErrors(prev => ({ ...prev, purpose: '' }));
    }
  };

  // Custom Password Requirements component with 5 points
  const CustomPasswordRequirements = ({ password }) => {
    const requirements = [
      { 
        label: 'At least 8 characters', 
        met: password.length >= 8,
        icon: '✓'
      },
      { 
        label: 'Uppercase letter', 
        met: /[A-Z]/.test(password),
        icon: '✓'
      },
      { 
        label: 'Lowercase letter', 
        met: /[a-z]/.test(password),
        icon: '✓'
      },
      { 
        label: 'Number', 
        met: /\d/.test(password),
        icon: '✓'
      },
      { 
        label: 'Special character', 
        met: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password),
        icon: '✓'
      }
    ];
    
    const metCount = requirements.filter(req => req.met).length;
    
    return (
      <div className="mt-2">
        <div className="text-xs font-medium text-gray-700 mb-1.5 text-left">
          Password requirements ({metCount}/5):
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
          {requirements.map((req, index) => (
            <div key={index} className="flex items-center space-x-1.5">
              <div className={`flex-shrink-0 w-4 h-4 rounded-full flex items-center justify-center ${
                req.met ? 'bg-green-100' : 'bg-gray-100'
              }`}>
                <span className={`text-xs ${req.met ? 'text-green-600 font-bold' : 'text-gray-400'}`}>
                  {req.met ? '✓' : '○'}
                </span>
              </div>
              <span className={`text-xs ${req.met ? 'text-green-700' : 'text-gray-600'}`}>
                {req.label}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Real API Integration function - Keep original working code
  const registerUser = async () => {
    // Prepare request body as per API specification
    const requestBody = {
      name: formData.name,
      email: formData.email,
      password: formData.password,
      mobile: formData.mobile
    };

    try {
      // Keep original API call that was working
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/auth/start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();
      
      if (!response.ok) {
        // Handle non-200 responses - show the exact error message from API
        let errorMessage = 'Registration failed';
        
        // Try to get error message from response data
        if (data && data.message) {
          errorMessage = data.message;
        } else if (data && data.error) {
          errorMessage = data.error;
        } else if (data && typeof data === 'string') {
          errorMessage = data;
        } else if (response.status === 400) {
          errorMessage = 'Bad request. Please check your information.';
        } else if (response.status === 401) {
          errorMessage = 'Unauthorized. Please check your credentials.';
        } else if (response.status === 403) {
          errorMessage = 'Forbidden. You do not have permission.';
        } else if (response.status === 409) {
          errorMessage = 'User already exists with this email or mobile.';
        } else if (response.status === 500) {
          errorMessage = 'Server error. Please try again later.';
        } else {
          errorMessage = `Registration failed with status: ${response.status}`;
        }
        
        throw new Error(errorMessage);
      }
      
      // Check if response has the expected structure
      if (!data.success || !data.userId || !data.otpTxnId) {
        // If response doesn't have expected structure but is 200 OK
        let errorMessage = 'Invalid response from server';
        if (data && data.message) {
          errorMessage = data.message;
        }
        throw new Error(errorMessage);
      }
      
      return data;
    } catch (error) {
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        // Network error or CORS issue detected
      }
      
      throw error;
    }
  };

// In handleSubmit function, update the navigate method:

const handleSubmit = async (e) => {
  e.preventDefault();
  
  // Mark all fields as touched when submitting
  const allFields = ['purpose', 'name', 'email', 'password', 'mobile'];
  const newTouchedFields = { ...touchedFields };
  allFields.forEach(field => {
    newTouchedFields[field] = true;
  });
  setTouchedFields(newTouchedFields);
  
  if (!validateAllFields()) {
    return;
  }

  try {
    setIsLoading(true);
    setApiError('');
    
    // Call the actual API
    const apiResponse = await registerUser();
    
    // Success - proceed exactly as before
    if (apiResponse.success === true && apiResponse.userId && apiResponse.otpTxnId) {
      clearDraft();
      setShowSuccessModal(true);
      
      setTimeout(() => {
        localStorage.setItem('tempUserData', JSON.stringify({
          ...formData,
          userId: apiResponse.userId,
          otpTxnId: apiResponse.otpTxnId
        }));
        
        // ✅ CHANGED: Send only userId and essential data to verification screen
        navigate('/verify_mobileNumber', { 
          state: { 
            userId: apiResponse.userId,      // Send userId explicitly
            otpTxnId: apiResponse.otpTxnId,  // Send OTP transaction ID
            mobile: formData.mobile          // Send mobile for display
            // ❌ Removed: formData, apiResponse (unnecessary data)
          } 
        });
      }, 2000);
    }
  } catch (error) {
    // Show the exact error message from API response
    const errorMessage = error.message || 'Registration failed. Please try again.';
    setApiError(errorMessage);
  } finally {
    setIsLoading(false);
  }
};
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gsg-teal/5">
      {isLoading && <LoadingOverlay message="Creating your account..." />}
      
      <SuccessModal
        isOpen={showSuccessModal}
        title="Registration Successful!"
        message="Your account has been created. You'll be redirected to verification in a moment."
        showCountdown={true}
        countdownDuration={2}
      />
      
      <AutoSaveIndicator 
        lastSaved={lastSaved} 
        isSaving={isSaving} 
        saveError={saveError} 
      />
      
      <div className="relative min-h-screen flex">
        {/* Left Section - Reduced width to give more space for center */}
        <div className="hidden lg:flex lg:w-1/3 relative bg-gradient-to-br from-gsg-teal/5 via-white to-white">
          <div className="relative z-10 flex flex-col justify-center items-start w-full h-full p-12">
            <div className="max-w-lg">
              {/* Logo & Title */}
              <div className={`mb-12 transition-all duration-1000`}>
                <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-gsg-teal to-gsg-teal-dark rounded-2xl mb-8 shadow-2xl shadow-gsg-teal/20">
                  <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                
                <h1 className="text-5xl font-bold text-gray-900 mb-6 leading-tight">
                  Start Your <span className="text-gsg-teal">Fundraiser</span>
                </h1>
                
                <p className="text-xl text-gray-600 leading-relaxed">
                  Join thousands who have successfully raised funds for their causes.
                </p>
              </div>
              
              {/* Features */}
              <div className={`transition-all duration-1000 delay-300`}>
                <div className="space-y-5">
                  {features.map((feature, index) => (
                    <div 
                      key={index}
                      className={`p-5 bg-white/95 backdrop-blur-sm rounded-2xl border-2 transition-all duration-500 
                        ${currentFeature === index ? 'border-gsg-teal/30 shadow-xl shadow-gsg-teal/10 scale-[1.02]' : 
                          'border-gray-100 shadow-sm hover:border-gray-200'
                        }
                      `}
                    >
                      <div className="flex items-start space-x-4">
                        <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center flex-shrink-0 shadow-md`}>
                          <span className="text-2xl">{feature.icon}</span>
                        </div>
                        <div>
                          <h4 className="font-bold text-gray-900 text-lg mb-1.5">{feature.title}</h4>
                          <p className="text-gray-600 text-sm leading-relaxed">{feature.description}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Center Content - Now takes more width and is truly centered */}
        <div className="w-full lg:w-2/3 flex flex-col justify-center items-center py-6 px-4 lg:px-8">
          <div className="w-full max-w-3xl mx-auto">
            {/* Centered Heading Section */}
            <div className="text-center mb-10 w-full">
              <h2 className="text-3xl font-bold text-gray-900 tracking-tight">Create Your Fundraiser Account</h2>
              <p className="text-gray-600 mt-3 text-lg">Fill in all details to get started</p>
            </div>
            
            {/* Progress Indicator */}
            <div className="flex items-center justify-center space-x-12 mb-10 w-full">
              {[1, 2].map((item, index) => (
                <div key={index} className="flex flex-col items-center">
                  <div className={`
                    w-12 h-12 rounded-full flex items-center justify-center text-base font-bold
                    ${index + 1 === 1 ? 'bg-gsg-teal text-white shadow-lg' : 
                      index + 1 < 1 ? 'bg-green-500 text-white' : 'bg-gray-100 text-gray-500'}
                  `}>
                    {index + 1}
                  </div>
                  <div className="text-sm font-semibold text-gray-700 mt-3">
                    {index === 0 ? 'Registration' : 'Verification'}
                  </div>
                  <div className="h-1 w-full bg-gray-200 mt-3 rounded-full">
                    <div className={`h-1 w-full ${index + 1 < 1 ? 'bg-green-500' : index + 1 === 1 ? 'bg-gsg-teal' : 'bg-gray-200'} rounded-full`}></div>
                  </div>
                </div>
              ))}
            </div>

            {/* Form Container - Centered with max width */}
            <div className="w-full flex justify-center">
              <div className="bg-white rounded-xl shadow-lg border border-gray-100 w-full max-w-2xl">
                {/* Header */}
                <div className="px-6 py-5 border-b border-gray-100 bg-gradient-to-r from-gsg-teal/5 to-white">
                  <div className="flex items-center space-x-3">
                    <div className={`w-10 h-10 rounded-lg bg-gradient-to-br from-gsg-teal to-gsg-teal-dark flex items-center justify-center shadow-sm`}>
                      <span className="text-lg text-white">
                        📝
                      </span>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">
                        Registration Details
                      </h3>
                      <p className="text-xs text-gray-600 mt-0.5">
                        Step 1 of 2: Fill all details below
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Form Content */}
                <div className="px-6 py-5">
                  {apiError && typeof apiError === 'object' ? (
                    <ErrorMessage message={apiError.message} type={apiError.type} />
                  ) : apiError ? (
                    <ErrorMessage message={apiError} type="error" />
                  ) : null}

                  <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Purpose Selection */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2.5 text-left">
                        What are you raising funds for? <span className="text-red-500">*</span>
                      </label>
                      
                      <div className="relative">
                        <div 
                          ref={triggerRef}
                          className={`w-full px-4 py-3.5 bg-white border-2 rounded-lg cursor-pointer transition-all duration-200 hover:border-gsg-teal ${
                            fieldErrors.purpose && touchedFields.purpose ? 'border-red-500' : 'border-gray-200'
                          }`}
                          onClick={() => {
                            setShowPurposeDropdown(!showPurposeDropdown);
                          }}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              {selectedPurpose ? (
                                <>
                                  <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${selectedPurpose.color} flex items-center justify-center`}>
                                    <span className="text-lg">{selectedPurpose.icon}</span>
                                  </div>
                                  <div>
                                    <span className="font-semibold text-gray-800 text-sm">{selectedPurpose.label}</span>
                                    <p className="text-xs text-gray-500 mt-0.5">{selectedPurpose.description}</p>
                                  </div>
                                </>
                              ) : (
                                <span className="text-gray-500 text-sm">Select a purpose</span>
                              )}
                            </div>
                            <svg className={`w-4 h-4 text-gray-400 transition-transform ${showPurposeDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                            </svg>
                          </div>
                        </div>
                        
                        {/* Dropdown Container */}
                        {showPurposeDropdown && (
                          <div 
                            ref={dropdownRef}
                            className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden"
                            style={{ maxHeight: '280px' }}
                          >
                            <div className="sticky top-0 bg-white px-4 py-2.5 border-b border-gray-200">
                              <h4 className="font-semibold text-gray-700 text-left text-sm">Select Purpose</h4>
                              <p className="text-xs text-gray-500 mt-0.5 text-left">{purposeOptions.length} options</p>
                            </div>
                            <div className="overflow-y-auto" style={{ maxHeight: '230px' }}>
                              {purposeOptions.map((option) => (
                                <div
                                  key={option.value}
                                  className={`p-2.5 cursor-pointer transition-all hover:bg-gray-50 border-b border-gray-100 last:border-b-0 ${
                                    selectedPurpose?.value === option.value ? 'bg-gsg-teal/5' : ''
                                  }`}
                                  onClick={() => handlePurposeSelect(option)}
                                >
                                  <div className="flex items-center space-x-3">
                                    <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${option.color} flex items-center justify-center flex-shrink-0`}>
                                      <span className="text-lg">{option.icon}</span>
                                    </div>
                                    <div className="min-w-0">
                                      <div className="font-medium text-gray-800 text-sm truncate">{option.label}</div>
                                      <div className="text-xs text-gray-500 truncate">{option.description}</div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                      
                      {fieldErrors.purpose && touchedFields.purpose && (
                        <p className="mt-1 text-sm text-red-600 text-left">
                          {fieldErrors.purpose}
                        </p>
                      )}
                    </div>

                    {/* Name and Email in Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Name Field */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-800 mb-2 text-left">
                          Full Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          onBlur={() => handleBlur('name')}
                          placeholder="Enter your name"
                          className={`
                            w-full px-3.5 py-3 rounded-lg border-2 transition-all duration-300
                            focus:outline-none focus:ring-1 focus:ring-gsg-teal/30 focus:border-gsg-teal
                            placeholder:text-gray-400 text-gray-900 text-sm
                            ${fieldErrors.name && touchedFields.name 
                              ? 'border-red-500 focus:border-red-500 focus:ring-red-500/30' 
                              : 'border-gray-200 hover:border-gray-300'
                            }
                          `}
                          maxLength={FIELD_LIMITS.name.max}
                        />
                        {fieldErrors.name && touchedFields.name && (
                          <p className="mt-1 text-xs text-red-600 text-left">
                            {fieldErrors.name}
                          </p>
                        )}
                      </div>

                      {/* Email Field */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-800 mb-2 text-left">
                          Email Address <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          onBlur={() => handleBlur('email')}
                          placeholder="Enter your email"
                          className={`
                            w-full px-3.5 py-3 rounded-lg border-2 transition-all duration-300
                            focus:outline-none focus:ring-1 focus:ring-gsg-teal/30 focus:border-gsg-teal
                            placeholder:text-gray-400 text-gray-900 text-sm
                            ${fieldErrors.email && touchedFields.email 
                              ? 'border-red-500 focus:border-red-500 focus:ring-red-500/30' 
                              : 'border-gray-200 hover:border-gray-300'
                            }
                          `}
                          maxLength={FIELD_LIMITS.email.max}
                        />
                        {fieldErrors.email && touchedFields.email && (
                          <p className="mt-1 text-xs text-red-600 text-left">
                            {fieldErrors.email}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Password and Mobile in Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Password Field */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-800 mb-2 text-left">
                          Password <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="password"
                          name="password"
                          value={formData.password}
                          onChange={handleChange}
                          onBlur={() => handleBlur('password')}
                          placeholder="Create secure password"
                          className={`
                            w-full px-3.5 py-3 rounded-lg border-2 transition-all duration-300
                            focus:outline-none focus:ring-1 focus:ring-gsg-teal/30 focus:border-gsg-teal
                            placeholder:text-gray-400 text-gray-900 text-sm
                            ${fieldErrors.password && touchedFields.password 
                              ? 'border-red-500 focus:border-red-500 focus:ring-red-500/30' 
                              : 'border-gray-200 hover:border-gray-300'
                            }
                          `}
                          maxLength={FIELD_LIMITS.password.max}
                        />
                        {fieldErrors.password && touchedFields.password && (
                          <p className="mt-1 text-xs text-red-600 text-left">
                            {fieldErrors.password}
                          </p>
                        )}
                      </div>

                      {/* Mobile Field */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-800 mb-2 text-left">
                          Mobile Number <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="tel"
                          name="mobile"
                          value={formData.mobile}
                          onChange={handleChange}
                          onBlur={() => handleBlur('mobile')}
                          placeholder="10-digit mobile number"
                          className={`
                            w-full px-3.5 py-3 rounded-lg border-2 transition-all duration-300
                            focus:outline-none focus:ring-1 focus:ring-gsg-teal/30 focus:border-gsg-teal
                            placeholder:text-gray-400 text-gray-900 text-sm
                            ${fieldErrors.mobile && touchedFields.mobile 
                              ? 'border-red-500 focus:border-red-500 focus:ring-red-500/30' 
                              : 'border-gray-200 hover:border-gray-300'
                            }
                          `}
                          maxLength={FIELD_LIMITS.mobile.max}
                        />
                        {fieldErrors.mobile && touchedFields.mobile && (
                          <p className="mt-1 text-xs text-red-600 text-left">
                            {fieldErrors.mobile}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Custom Password Requirements with 5 points */}
                    <div className="mt-2 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                      <CustomPasswordRequirements password={formData.password} />
                    </div>
                    
                    {/* Mobile Info */}
                    <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-start space-x-2">
                        <svg className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <p className="text-xs text-blue-700 text-left">
                          We'll send a verification code to your mobile number to secure your account.
                        </p>
                      </div>
                    </div>
                    
                    {/* Submit Button */}
                    <div className="mt-6 pt-5 border-t border-gray-100">
                      <button
                        type="submit"
                        disabled={!isFormValid || isLoading}
                        className={`
                          w-full px-6 py-2.5 rounded-lg transition-all duration-300 font-semibold text-sm
                          ${!isFormValid || isLoading
                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            : 'bg-gradient-to-r from-gsg-teal to-gsg-teal-dark text-white hover:shadow-md hover:shadow-gsg-teal/25'
                          }
                          flex items-center justify-center
                        `}
                      >
                        {isLoading ? (
                          <>
                            <svg className="animate-spin h-4 w-4 mr-2 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Creating Account...
                          </>
                        ) : 'Register & Verify Mobile'}
                      </button>
                    </div>
                  </form>

                  {/* Login Link */}
                  <div className="text-center pt-4 mt-4 border-t border-gray-100">
                    <p className="text-xs text-gray-600">
                      Already have an account?{' '}
                      <a href="#" className="font-semibold text-gsg-teal hover:text-gsg-teal-dark hover:underline transition-colors">
                        Login here
                      </a>
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer - Centered */}
            <div className="text-center mt-8 w-full">
              <p className="text-xs text-gray-500">
                By continuing, you agree to our Terms & Privacy Policy
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FundraiserForm;